/**
 * Client-side facet helpers for leads list (corridor, truck).
 */

import {
  formatTruckLabelWithLoad,
  getLeadCorridorBadge,
} from "./leadJobCockpit.js";
import {
  getLeadTruckLoadLabel,
  getLeadTruckRecommendation,
} from "./leadOperationalStatus.js";

export const LEAD_FILTER_NONE = "__none__";
export const LEAD_FILTER_ALL = "";

/**
 * @param {object} lead
 * @returns {string} corridor id or LEAD_FILTER_NONE
 */
export function getLeadCorridorFilterKey(lead) {
  return getLeadCorridorBadge(lead) || LEAD_FILTER_NONE;
}

/**
 * Normalized truck size key for filtering (e.g. "1_ton").
 * @param {object} lead
 * @returns {string}
 */
export function getLeadTruckFilterKey(lead) {
  const size = getLeadTruckRecommendation(lead);
  if (!size) return LEAD_FILTER_NONE;
  return String(size).trim().toLowerCase().replace(/\s+/g, "_");
}

/**
 * @param {object} lead
 * @returns {string}
 */
export function getLeadTruckFilterLabel(lead) {
  const size = getLeadTruckRecommendation(lead);
  if (!size) return "No truck";
  return formatTruckLabelWithLoad(size, getLeadTruckLoadLabel(lead));
}

/**
 * @param {object[]} leads
 * @returns {{ value: string, label: string, count: number }[]}
 */
export function collectCorridorFilterOptions(leads = []) {
  const counts = new Map();
  for (const lead of leads) {
    const actual = lead?.primaryLead || lead;
    const key = getLeadCorridorFilterKey(actual);
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  return [...counts.entries()]
    .map(([value, count]) => ({
      value,
      label: value === LEAD_FILTER_NONE ? "No corridor" : value,
      count,
    }))
    .sort((a, b) => {
      if (a.value === LEAD_FILTER_NONE) return 1;
      if (b.value === LEAD_FILTER_NONE) return -1;
      return a.label.localeCompare(b.label);
    });
}

/**
 * @param {object[]} leads
 * @returns {{ value: string, label: string, count: number }[]}
 */
export function collectTruckFilterOptions(leads = []) {
  const byKey = new Map();
  for (const lead of leads) {
    const actual = lead?.primaryLead || lead;
    const value = getLeadTruckFilterKey(actual);
    const existing = byKey.get(value);
    if (existing) {
      existing.count += 1;
      continue;
    }
    byKey.set(value, {
      value,
      label:
        value === LEAD_FILTER_NONE
          ? "No truck"
          : getLeadTruckFilterLabel(actual),
      count: 1,
    });
  }
  return [...byKey.values()].sort((a, b) => {
    if (a.value === LEAD_FILTER_NONE) return 1;
    if (b.value === LEAD_FILTER_NONE) return -1;
    return a.label.localeCompare(b.label);
  });
}

/**
 * @param {object} lead
 * @param {string|null|undefined} corridorFilter
 * @returns {boolean}
 */
export function leadMatchesCorridorFilter(lead, corridorFilter) {
  if (!corridorFilter) return true;
  return getLeadCorridorFilterKey(lead?.primaryLead || lead) === corridorFilter;
}

/**
 * @param {object} lead
 * @param {string|null|undefined} truckFilter
 * @returns {boolean}
 */
export function leadMatchesTruckFilter(lead, truckFilter) {
  if (!truckFilter) return true;
  return getLeadTruckFilterKey(lead?.primaryLead || lead) === truckFilter;
}
